/**
 * Web Capability Policy Page (REQ-WEB-007 / ADR-118)
 *
 * Global DR policy of the native `web` capability (search + delegated fetch):
 * provider selection, hardening defaults (JavaScript OFF, safe search strict,
 * reputation fail-closed) and activation parameters. SystemAdmin only
 * (Settings → System section).
 *
 * Security notes surfaced in the UI: the platform never connects to the web
 * (delegated download in the provider sandbox) and DGV classification is a
 * fixed minimum (cannot be disabled).
 */

import { useEffect, useMemo, useState } from 'react';
import { Globe, Loader2, Save } from 'lucide-react';
import { Card } from '@/components/ui';
import { toast } from 'sonner';
import { useWebPolicy, useUpdateWebPolicy, type WebPolicySettings } from '@/hooks/use-web-policy';

// =============================================================================
// Helpers
// =============================================================================

function parseList(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function validationMessage(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && 'errors' in detail) {
    const errors = (detail as { errors?: string[] }).errors;
    if (errors?.length) return errors[0];
  }
  return 'Could not save the web capability policy.';
}

// =============================================================================
// Component
// =============================================================================

export function WebCapabilityPage() {
  const { data: policy, isLoading, error } = useWebPolicy();
  const updateMutation = useUpdateWebPolicy();
  const [form, setForm] = useState<WebPolicySettings | null>(null);

  useEffect(() => {
    if (policy) setForm(JSON.parse(JSON.stringify(policy.settings)) as WebPolicySettings);
  }, [policy]);

  const providers = policy?.providers ?? [];
  const searchProviders = providers.filter((p) => p.kind === 'search');
  const extractionProviders = providers.filter((p) => p.kind === 'extraction');

  const dirty = useMemo(
    () => form != null && JSON.stringify(form) !== JSON.stringify(policy?.settings),
    [form, policy]
  );

  const patchSection = (section: string, patch: Record<string, unknown>) => {
    setForm((prev) =>
      prev
        ? {
            ...prev,
            [section]: {
              ...((prev[section] as Record<string, unknown>) ?? {}),
              ...patch,
            },
          }
        : prev
    );
  };

  // Sprint 269: override por herramienta (null = quitar la clave).
  const patchPerTool = (tool: string, value: number | null) => {
    setForm((prev) => {
      if (!prev) return prev;
      const limits = { ...((prev.limits as Record<string, unknown>) ?? {}) };
      const perTool = { ...((limits.caps_per_tool_turn as Record<string, number>) ?? {}) };
      if (value === null) {
        delete perTool[tool];
      } else {
        perTool[tool] = value;
      }
      if (Object.keys(perTool).length > 0) {
        limits.caps_per_tool_turn = perTool;
      } else {
        delete limits.caps_per_tool_turn;
      }
      return { ...prev, limits };
    });
  };

  const handleSave = async () => {
    if (!form) return;
    try {
      await updateMutation.mutateAsync(form);
      toast.success('Web capability policy updated');
    } catch (err) {
      toast.error(validationMessage(err));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading web capability policy…
      </div>
    );
  }

  if (error || !form) {
    return (
      <div className="py-16 text-center text-sm text-muted-foreground">
        Could not load the web capability policy (System Admin required).
      </div>
    );
  }

  const selection = (form.selection ?? {}) as NonNullable<WebPolicySettings['selection']>;
  const search = (form.search ?? {}) as NonNullable<WebPolicySettings['search']>;
  const fetch = (form.fetch ?? {}) as NonNullable<WebPolicySettings['fetch']>;
  // Sprint 269: límites anti-abuso (global→tenant→hive→agente; min-wins).
  const limits = (form.limits ?? {}) as NonNullable<WebPolicySettings['limits']>;
  // `security` sin uso visible hasta OPS-2026-155 (selector de reputación oculto;
  // DGV es fijo mínimo y no se edita). Se restaura con el bloque del selector.

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <Globe className="h-5 w-5" />
          Web Capability
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Global DR policy for agent web search and delegated web download. The platform never
          connects to the web: downloads run inside the managed provider sandbox and the returned
          content is sanitized by Klviar. A future per-tenant policy will reuse this model.
        </p>
      </div>

      <Card className="space-y-4 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Provider selection
        </h2>
        {providers.length === 0 && (
          <p className="text-xs text-amber-600">
            No providers registered yet (implemented in streams B/C). Defaults are shown.
          </p>
        )}
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="search-provider" className="mb-1 block text-sm font-medium">
              Search provider
            </label>
            <select
              id="search-provider"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={selection.search_provider ?? ''}
              onChange={(e) => patchSection('selection', { search_provider: e.target.value })}
            >
              {(searchProviders.length > 0 ? searchProviders : [{ slug: selection.search_provider ?? '', display_name: selection.search_provider ?? '' }]).map(
                (p) => (
                  <option key={p.slug} value={p.slug}>
                    {p.display_name || p.slug}
                  </option>
                )
              )}
            </select>
          </div>
          <div>
            <label htmlFor="extraction-provider" className="mb-1 block text-sm font-medium">
              Download (extraction) provider
            </label>
            <select
              id="extraction-provider"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={selection.extraction_provider ?? ''}
              onChange={(e) => patchSection('selection', { extraction_provider: e.target.value })}
            >
              {(extractionProviders.length > 0 ? extractionProviders : [{ slug: selection.extraction_provider ?? '', display_name: selection.extraction_provider ?? '' }]).map(
                (p) => (
                  <option key={p.slug} value={p.slug}>
                    {p.display_name || p.slug}
                  </option>
                )
              )}
            </select>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            id="eu-only"
            type="checkbox"
            checked={selection.eu_only ?? true}
            onChange={(e) => patchSection('selection', { eu_only: e.target.checked })}
          />
          Restrict provider selection to EU processing (fixed minimum in production)
        </label>
      </Card>

      <Card className="space-y-4 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Search defaults
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="max-results" className="mb-1 block text-sm font-medium">
              Max results per search (1–25)
            </label>
            <input
              id="max-results"
              type="number"
              min={1}
              max={25}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={search.max_results ?? 10}
              onChange={(e) => patchSection('search', { max_results: Number(e.target.value) })}
            />
          </div>
          <div>
            <label htmlFor="safe-search" className="mb-1 block text-sm font-medium">
              Safe search
            </label>
            <select
              id="safe-search"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={search.safe_search ?? 'strict'}
              onChange={(e) => patchSection('search', { safe_search: e.target.value })}
            >
              <option value="off">Off</option>
              <option value="moderate">Moderate</option>
              <option value="strict">Strict</option>
            </select>
          </div>
        </div>
      </Card>

      <Card className="space-y-4 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Download hardening
        </h2>
        <label className="flex items-center gap-2 text-sm">
          <input
            id="javascript-enabled"
            type="checkbox"
            checked={fetch.javascript_enabled ?? false}
            onChange={(e) => patchSection('fetch', { javascript_enabled: e.target.checked })}
          />
          JavaScript enabled in the managed browser (OFF by default; execution stays in the
          provider sandbox)
        </label>
        <div>
          <label htmlFor="js-allowed-domains" className="mb-1 block text-sm font-medium">
            Domains allowed to render JavaScript (comma separated)
          </label>
          <input
            id="js-allowed-domains"
            type="text"
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            value={(fetch.js_allowed_domains ?? []).join(', ')}
            onChange={(e) => patchSection('fetch', { js_allowed_domains: parseList(e.target.value) })}
          />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="page-timeout" className="mb-1 block text-sm font-medium">
              Page timeout (seconds)
            </label>
            <input
              id="page-timeout"
              type="number"
              min={1}
              max={120}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={fetch.page_timeout_seconds ?? 20}
              onChange={(e) =>
                patchSection('fetch', { page_timeout_seconds: Number(e.target.value) })
              }
            />
          </div>
          <div>
            <label htmlFor="session-ttl" className="mb-1 block text-sm font-medium">
              Provider session TTL (seconds)
            </label>
            <input
              id="session-ttl"
              type="number"
              min={10}
              max={28800}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={fetch.session_ttl_seconds ?? 120}
              onChange={(e) =>
                patchSection('fetch', { session_ttl_seconds: Number(e.target.value) })
              }
            />
          </div>
        </div>
      </Card>

      <Card className="space-y-4 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Security
        </h2>
        {/*
          Reputation fail-mode oculto hasta OPS-2026-155 (decisión 14-Oct-2026,
          ISS-576): sin servicio de reputación implementado, el parámetro no se
          muestra. El backend mantiene el knob (`closed` explícito gana por
          techo) y el default `open_degraded`. Restaurar este bloque al
          activar el servicio.
        */}
        <p className="text-xs text-muted-foreground">
          Data governance (DGV) classification of queries and downloaded content is a fixed
          minimum and cannot be disabled.
        </p>
      </Card>

      <Card className="space-y-4 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Anti-abuse limits
        </h2>
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label htmlFor="caps-per-turn" className="mb-1 block text-sm font-medium">
              Calls per turn window
            </label>
            <input
              id="caps-per-turn"
              type="number"
              min={1}
              max={100}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={limits.caps_per_turn ?? 8}
              onChange={(e) => patchSection('limits', { caps_per_turn: Number(e.target.value) })}
            />
          </div>
          <div>
            <label htmlFor="caps-per-day" className="mb-1 block text-sm font-medium">
              Calls per conversation-day
            </label>
            <input
              id="caps-per-day"
              type="number"
              min={1}
              max={10000}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={limits.caps_per_day ?? 200}
              onChange={(e) => patchSection('limits', { caps_per_day: Number(e.target.value) })}
            />
          </div>
          <div>
            <label htmlFor="burst-window" className="mb-1 block text-sm font-medium">
              Burst window (seconds)
            </label>
            <input
              id="burst-window"
              type="number"
              min={10}
              max={3600}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={limits.burst_window_seconds ?? 120}
              onChange={(e) =>
                patchSection('limits', { burst_window_seconds: Number(e.target.value) })
              }
            />
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="caps-per-tool-search" className="mb-1 block text-sm font-medium">
              web_search burst override (optional)
            </label>
            <input
              id="caps-per-tool-search"
              type="number"
              min={1}
              max={100}
              placeholder={`default (${limits.caps_per_turn ?? 8})`}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={limits.caps_per_tool_turn?.web_search ?? ''}
              onChange={(e) =>
                patchPerTool('web_search', e.target.value === '' ? null : Number(e.target.value))
              }
            />
          </div>
          <div>
            <label htmlFor="caps-per-tool-fetch" className="mb-1 block text-sm font-medium">
              web_fetch burst override (optional)
            </label>
            <input
              id="caps-per-tool-fetch"
              type="number"
              min={1}
              max={100}
              placeholder={`default (${limits.caps_per_turn ?? 8})`}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={limits.caps_per_tool_turn?.web_fetch ?? ''}
              onChange={(e) =>
                patchPerTool('web_fetch', e.target.value === '' ? null : Number(e.target.value))
              }
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Effective per level (global → tenant → hive → agent; most restrictive wins): these
          platform values are the ceiling. Tenant, hive and agent levels can only tighten them
          (Capacidades → agent/hive settings, tenant capability policy).
        </p>
      </Card>

      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {policy?.updated_by ? `Last change by ${policy.updated_by}` : 'No explicit policy yet'}
        </span>
        <button
          type="button"
          onClick={handleSave}
          disabled={!dirty || updateMutation.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {updateMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          Save policy
        </button>
      </div>
    </div>
  );
}
